const FIXED_VAT_RATE = 20.0;
const CALL_OUT_RATE = 80; // 1 hour = call out = £80 fixed
export const HOURS_PER_DAY = 8; // 1 day = 8 hours (changed from 7, Jul 2026)

// Client types with their rules — hourly rates are LOCKED company-wide (not editable in-app)
let CLIENT_PRICING = {
  "Private": { hourly_rate: 54, commission_type: "none", materials_rule: "private" },
  "Bairstow Eves": { hourly_rate: 67, commission_type: "20+vat", materials_rule: "commission" },
  "Hamptons": { hourly_rate: 67, commission_type: "12.5+vat", materials_rule: "commission" },
  "Gladstone": { hourly_rate: 54, commission_type: "none", materials_rule: "private" },
  "Keatons": { hourly_rate: 67, commission_type: "none", materials_rule: "private" },
  "Grant J Bates": { hourly_rate: 60, commission_type: "none", materials_rule: "private" },
};

const CLIENT_RULES = CLIENT_PRICING;
const FIXED_LABOUR_RATE = 50.0;

// Load settings from API — returns a promise so callers can await
let _loadPromise = null;
export function loadPricingSettings() {
  if (_loadPromise) return _loadPromise;
  _loadPromise = (async () => {
    try {
      const API = process.env.REACT_APP_BACKEND_URL;
      const resp = await fetch(`${API}/api/settings/pricing`);
      if (resp.ok) {
        const data = await resp.json();
        // Only merge entries that have the new format
        for (const [key, val] of Object.entries(data)) {
          if (val && typeof val === "object" && "hourly_rate" in val) {
            CLIENT_PRICING[key] = {
              hourly_rate: val.hourly_rate || 54,
              commission_type: val.commission_type || "none",
              materials_rule: val.materials_rule || "private",
            };
          }
        }
      }
    } catch { /* use defaults */ }
  })();
  return _loadPromise;
}

export function getPricing(clientType) {
  return CLIENT_PRICING[clientType] || CLIENT_PRICING["Private"] || { hourly_rate: 54, commission_type: "none", materials_rule: "private" };
}

export function getAllClientTypes() {
  return Object.keys(CLIENT_PRICING);
}

// Compute action rate. Call out (£80) is only when explicitly set via price field.
// Regular 1hr uses the client hourly rate like any other duration.
// `lockedRate` (quote.locked_hourly_rate) freezes sent quotes at their original rate.
export function computeActionRate(hours, clientType, lockedRate) {
  const h = parseFloat(hours) || 0;
  const locked = parseFloat(lockedRate) || 0;
  const rate = locked > 0 ? locked : (getPricing(clientType).hourly_rate || 54);
  return Math.round(h * rate * 100) / 100;
}

// Materials: Private = inc VAT ÷ 1.2 + markup. Commission = inc VAT ÷ 0.85.
export function computeMaterialsExVat(incVatTotal, clientType, markupPercent = 0) {
  const p = getPricing(clientType);
  if (p.materials_rule === "commission") {
    return Math.round((incVatTotal / 0.85) * 100) / 100;
  }
  const exVat = Math.round((incVatTotal / 1.2) * 100) / 100;
  if (markupPercent > 0) {
    return Math.round(exVat * (1 + markupPercent / 100) * 100) / 100;
  }
  return exVat;
}

export function computeTotals(draft, items = []) {
  const lockedRate = parseFloat(draft.locked_hourly_rate) || 0;

  // Labour
  const actions = draft.actions || [];
  let labourTotal = actions.reduce((sum, a) => {
    if (typeof a !== "object" || a.is_sub) return sum;
    const hrs = parseFloat(a.hours) || 0;
    const price = a.price != null && a.price > 0 ? a.price : computeActionRate(hrs, draft.client_type, lockedRate);
    return sum + price;
  }, 0);
  labourTotal = Math.round(labourTotal * 100) / 100;

  if (actions.filter(a => typeof a === "object" && !a.is_sub).length === 0 && draft.labour_hours > 0) {
    labourTotal = computeActionRate(draft.labour_hours, draft.client_type, lockedRate);
  }

  // Materials: sum raw inc VAT
  const rawMaterialsIncVat = items.reduce((sum, item) => {
    return sum + Math.round((parseFloat(item.unit_price) || 0) * (parseInt(item.qty) || 0) * 100) / 100;
  }, 0);

  // Convert to ex VAT based on client type
  const markupPercent = parseFloat(draft.materials_markup) || 0;
  const materialsExVat = computeMaterialsExVat(rawMaterialsIncVat, draft.client_type, markupPercent);

  // Parking — user enters rate inc VAT, apply same rules as materials
  const parkingIncVat = Math.round(((parseFloat(draft.parking_hours) || 0) * (parseFloat(draft.parking_rate) || 0)) * 100) / 100;
  const parkingExVat = parkingIncVat > 0 ? computeMaterialsExVat(parkingIncVat, draft.client_type, 0) : 0;

  // Congestion charge — £18 flat, always ÷ 0.85 for ALL client types
  const congestionDays = parseFloat(draft.congestion_days) || 0;
  const congestionIncVat = congestionDays * CONGESTION_CHARGE;
  const congestionExVat = congestionIncVat > 0 ? Math.round((congestionIncVat / 0.85) * 100) / 100 : 0;

  const totalExVat = Math.round((labourTotal + materialsExVat + parkingExVat + congestionExVat) * 100) / 100;
  const vatAmount = draft.vat_enabled !== false ? Math.round(totalExVat * (FIXED_VAT_RATE / 100) * 100) / 100 : 0;
  const totalIncVat = Math.round((totalExVat + vatAmount) * 100) / 100;

  return {
    labourTotal,
    materialsSubtotal: rawMaterialsIncVat,
    materialsExVat,
    parkingIncVat,
    parkingExVat,
    congestionIncVat,
    congestionExVat,
    congestionDays,
    materialsTotal: materialsExVat,
    totalExVat,
    vatRate: FIXED_VAT_RATE,
    vatAmount,
    totalIncVat,
  };
}

export function formatCurrency(value) {
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
    minimumFractionDigits: 2,
  }).format(value || 0);
}

// Normalize a string for flexible address search: strip punctuation, collapse spaces, lowercase
export function normalizeSearch(str) {
  return (str || "").replace(/[,\-\.'"]/g, " ").replace(/\s+/g, " ").trim().toLowerCase();
}

// Check if all search words appear anywhere in the target string (word-order flexible)
export function flexMatch(target, search) {
  const normTarget = normalizeSearch(target);
  const words = normalizeSearch(search).split(" ").filter(Boolean);
  return words.every(w => normTarget.includes(w));
}


export { CLIENT_RULES, CLIENT_PRICING, FIXED_LABOUR_RATE, FIXED_VAT_RATE, CALL_OUT_RATE };

// London Congestion Charge Zone postcodes (fully inside)
const CCZ_POSTCODES = new Set([
  "EC1", "EC2", "EC3", "EC4", "WC1", "WC2", "W1", "SW1", "SE1",
  "EC1A", "EC1M", "EC1N", "EC1P", "EC1R", "EC1V", "EC1Y",
  "EC2A", "EC2M", "EC2N", "EC2P", "EC2R", "EC2V", "EC2Y",
  "EC3A", "EC3M", "EC3N", "EC3P", "EC3R", "EC3V",
  "EC4A", "EC4M", "EC4N", "EC4P", "EC4R", "EC4V", "EC4Y",
  "WC1A", "WC1B", "WC1E", "WC1H", "WC1N", "WC1R", "WC1V", "WC1X",
  "WC2A", "WC2B", "WC2E", "WC2H", "WC2N", "WC2R",
  "W1B", "W1C", "W1D", "W1F", "W1G", "W1H", "W1J", "W1K", "W1S", "W1T", "W1U", "W1W",
  "SW1A", "SW1E", "SW1H", "SW1P", "SW1V", "SW1W", "SW1X", "SW1Y",
  "SE1",
]);

export const CONGESTION_CHARGE = 18;

export function isInCongestionZone(address) {
  if (!address) return false;
  // Extract postcode from address string
  const match = address.match(/\b([A-Z]{1,2}\d{1,2}[A-Z]?)\s*\d[A-Z]{2}\b/i);
  if (!match) return false;
  const district = match[1].toUpperCase();
  // Check full district (e.g. SW1A) then prefix (e.g. SW1) then area (e.g. EC1)
  if (CCZ_POSTCODES.has(district)) return true;
  // Try without trailing letter: EC1A → EC1
  const shorter = district.replace(/[A-Z]$/i, "");
  if (CCZ_POSTCODES.has(shorter)) return true;
  return false;
}
